<template>
  <div class="altitude-ortho-box-map d-flex flex-column flex-grow-1" style="min-height: 240px">
    <div class="d-flex align-center px-2 py-1 flex-grow-0">
      <v-btn-toggle
        v-model="toolMode"
        dense
        mandatory
        class="mr-2"
        data-testid="altitude-ortho-tool-mode"
      >
        <v-btn small value="draw" :title="$t('altitude.estimateToolDraw')">
          <v-icon small>mdi-vector-polygon</v-icon>
        </v-btn>
        <v-btn small value="pan" :title="$t('altitude.estimateToolPan')">
          <v-icon small>mdi-hand-back-left</v-icon>
        </v-btn>
      </v-btn-toggle>
      <span class="caption text--secondary">
        {{
          L_map_m != null
            ? $t('altitude.estimateOrthoLMap', { m: formatNum(L_map_m) })
            : $t('altitude.estimateOrthoDrawHint')
        }}
      </span>
      <v-spacer />
      <v-btn
        x-small
        text
        :disabled="L_map_m == null"
        data-testid="altitude-ortho-clear"
        @click="clearBox"
      >
        {{ $t('altitude.estimateClearBox') }}
      </v-btn>
    </div>
    <div
      ref="mapEl"
      class="flex-grow-1 rounded"
      style="min-height: 220px; width: 100%; overflow: hidden"
      data-testid="altitude-estimate-ortho-map"
    />
  </div>
</template>

<script>
  import Map from 'ol/Map';
  import View from 'ol/View';
  import VectorLayer from 'ol/layer/Vector';
  import VectorSource from 'ol/source/Vector';
  import Feature from 'ol/Feature';
  import Polygon from 'ol/geom/Polygon';
  import Draw from 'ol/interaction/Draw';
  import DragPan from 'ol/interaction/DragPan';
  import { defaults as defaultInteractions } from 'ol/interaction';
  import { defaults as defaultControls } from 'ol/control';
  import Style from 'ol/style/Style';
  import Stroke from 'ol/style/Stroke';
  import Fill from 'ol/style/Fill';
  import CircleStyle from 'ol/style/Circle';
  import DotSolarMapContext from '@/components/map/DotSolarMapContext';
  import { longestSideMetersFromLonLatRing } from '@/features/altitude-estimate/boxMetrics';
  import { polygonRingFromImgPosCoords } from '@/services/defect/defectMapIrAlignment';

  const DRAW_STYLE = new Style({
    stroke: new Stroke({ color: '#1976d2', width: 2 }),
    fill: new Fill({ color: 'rgba(25, 118, 210, 0.18)' }),
    image: new CircleStyle({
      radius: 5,
      fill: new Fill({ color: '#1976d2' }),
      stroke: new Stroke({ color: '#fff', width: 1 })
    })
  });

  const GUIDE_STYLE = new Style({
    stroke: new Stroke({
      color: '#ff9800',
      width: 2,
      lineDash: [8, 6]
    }),
    fill: new Fill({ color: 'rgba(255, 152, 0, 0.08)' }),
    image: new CircleStyle({
      radius: 4,
      fill: new Fill({ color: '#ff9800' }),
      stroke: new Stroke({ color: '#fff', width: 1 })
    })
  });

  export default {
    name: 'AltitudeOrthoBoxMap',
    props: {
      thermalImages: { type: Array, default: () => [] },
      site: { type: Object, default: null },
      /** 선택된 IR — footprint로 지도 맞춤 + 가이드 폴리곤 */
      focusImage: { type: Object, default: null }
    },
    data() {
      return {
        L_map_m: null,
        orthoBox: null,
        map: null,
        drawSource: null,
        guideSource: null,
        drawInteraction: null,
        dragPanInteraction: null,
        isSketching: false,
        /** 'draw' | 'pan' */
        toolMode: 'draw',
        spacePanHeld: false
      };
    },
    watch: {
      toolMode() {
        this.applyToolMode();
      },
      focusImage: {
        deep: true,
        handler(img) {
          this.syncFocusGuide(img);
        }
      },
      thermalImages: {
        deep: true,
        handler() {
          if (!this.focusImage) {
            this.fitFallbackView();
          }
        }
      },
      site() {
        if (!this.focusImage) {
          this.fitFallbackView();
        }
      }
    },
    mounted() {
      this.$nextTick(() => this.initMap());
      this._onKeyDown = evt => {
        if (evt.key === 'Escape') {
          this.cancelDrawing();
          return;
        }
        if (evt.code === 'Space' && !evt.repeat) {
          evt.preventDefault();
          this.spacePanHeld = true;
          this.applyToolMode();
        }
      };
      this._onKeyUp = evt => {
        if (evt.code === 'Space') {
          this.spacePanHeld = false;
          this.applyToolMode();
        }
      };
      window.addEventListener('keydown', this._onKeyDown);
      window.addEventListener('keyup', this._onKeyUp);
    },
    beforeDestroy() {
      if (this._onKeyDown) {
        window.removeEventListener('keydown', this._onKeyDown);
      }
      if (this._onKeyUp) {
        window.removeEventListener('keyup', this._onKeyUp);
      }
      this.destroyMap();
    },
    methods: {
      formatNum(v) {
        const n = Number(v);
        return Number.isFinite(n) ? n.toFixed(2) : '—';
      },

      /** Space 홀드 중이면 임시 손 모드 */
      effectivePanMode() {
        return this.toolMode === 'pan' || this.spacePanHeld;
      },

      applyToolMode() {
        const pan = this.effectivePanMode();
        if (this.drawInteraction) {
          this.drawInteraction.setActive(!pan);
        }
        if (this.dragPanInteraction) {
          this.dragPanInteraction.setActive(pan);
        }
        if (this.$refs.mapEl) {
          this.$refs.mapEl.style.cursor = pan ? 'grab' : 'crosshair';
        }
      },

      cancelDrawing() {
        if (this.drawInteraction && this.isSketching) {
          try {
            this.drawInteraction.abortDrawing();
          } catch (e) {
            /* ignore */
          }
          this.isSketching = false;
          return;
        }
        if (this.L_map_m != null) {
          this.clearBox();
        }
      },

      onMapContextMenu(evt) {
        evt.preventDefault();
        this.cancelDrawing();
      },

      initMap() {
        if (!this.$refs.mapEl || this.map) {
          return;
        }
        const layers = [
          ...DotSolarMapContext.getBaseLayers({ satelliteOn: true }),
          DotSolarMapContext.getSiteImageMapTileLayer()
        ];
        this.guideSource = new VectorSource();
        this.drawSource = new VectorSource();
        layers.push(
          new VectorLayer({
            source: this.guideSource,
            style: GUIDE_STYLE,
            zIndex: 15
          })
        );
        layers.push(
          new VectorLayer({
            source: this.drawSource,
            style: DRAW_STYLE,
            zIndex: 20
          })
        );
        // 기본 interactions에서 DragPan 제외 → 손 모드에서만 켬
        const interactions = defaultInteractions({
          dragPan: false,
          altShiftDragRotate: false,
          pinchRotate: false
        });
        this.dragPanInteraction = new DragPan();
        this.dragPanInteraction.setActive(false);
        interactions.push(this.dragPanInteraction);

        this.map = new Map({
          target: this.$refs.mapEl,
          layers,
          controls: defaultControls({ zoom: true, attribution: false }),
          interactions,
          view: new View({
            projection: 'EPSG:4326',
            center: [127.0, 37.5],
            zoom: 15
          })
        });
        this.drawInteraction = new Draw({
          source: this.drawSource,
          type: 'Polygon',
          style: DRAW_STYLE,
          minPoints: 3,
          maxPoints: 4
        });
        this.drawInteraction.on('drawstart', () => {
          this.drawSource.clear();
          this.isSketching = true;
          this.L_map_m = null;
          this.orthoBox = null;
          this.$emit('box-change', null);
        });
        this.drawInteraction.on('drawend', evt => {
          this.isSketching = false;
          this.onDrawEnd(evt.feature);
        });
        this.map.addInteraction(this.drawInteraction);
        this._onMapContextMenu = evt => this.onMapContextMenu(evt);
        this.map.getViewport().addEventListener('contextmenu', this._onMapContextMenu);
        this.applyToolMode();
        if (this.focusImage) {
          this.syncFocusGuide(this.focusImage);
        } else {
          this.fitFallbackView();
        }
        setTimeout(() => this.map && this.map.updateSize(), 80);
      },

      destroyMap() {
        if (this._onMapContextMenu && this.map) {
          this.map.getViewport().removeEventListener('contextmenu', this._onMapContextMenu);
          this._onMapContextMenu = null;
        }
        if (this.drawInteraction && this.map) {
          this.map.removeInteraction(this.drawInteraction);
        }
        if (this.map) {
          this.map.setTarget(null);
          this.map = null;
        }
        this.drawSource = null;
        this.guideSource = null;
        this.drawInteraction = null;
        this.dragPanInteraction = null;
        this.isSketching = false;
      },

      syncFocusGuide(img) {
        if (!this.map || !this.guideSource) {
          return;
        }
        this.guideSource.clear();
        const ring = polygonRingFromImgPosCoords(img?.imgPosCoords);
        if (!ring || ring.length < 4) {
          this.fitFallbackView();
          return;
        }
        const open = this.openRing(ring);
        const closed = open.concat([open[0]]);
        const feature = new Feature({
          geometry: new Polygon([closed])
        });
        this.guideSource.addFeature(feature);
        const extent = this.ringToExtent(open);
        if (extent) {
          this.map.getView().fit(extent, {
            padding: [48, 48, 48, 48],
            maxZoom: 20,
            duration: 250
          });
        }
      },

      openRing(ring) {
        const first = ring[0];
        const last = ring[ring.length - 1];
        const closed =
          first &&
          last &&
          Number(first[0]) === Number(last[0]) &&
          Number(first[1]) === Number(last[1]);
        return closed ? ring.slice(0, -1) : ring.slice();
      },

      fitFallbackView() {
        if (!this.map) {
          return;
        }
        const extent = this.resolveFitExtent();
        if (!extent) {
          return;
        }
        this.map.getView().fit(extent, {
          padding: [40, 40, 40, 40],
          maxZoom: 19,
          duration: 0
        });
      },

      resolveFitExtent() {
        const fromSite = this.extentFromGeometry(this.site?.location);
        if (fromSite) {
          return fromSite;
        }
        const rings = (this.thermalImages || [])
          .map(img => polygonRingFromImgPosCoords(img?.imgPosCoords))
          .filter(r => r && r.length >= 4);
        if (rings.length === 0) {
          return null;
        }
        let minLon = Infinity;
        let minLat = Infinity;
        let maxLon = -Infinity;
        let maxLat = -Infinity;
        rings.forEach(ring => {
          ring.forEach(c => {
            const lon = Number(c[0]);
            const lat = Number(c[1]);
            if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
              return;
            }
            minLon = Math.min(minLon, lon);
            minLat = Math.min(minLat, lat);
            maxLon = Math.max(maxLon, lon);
            maxLat = Math.max(maxLat, lat);
          });
        });
        if (!Number.isFinite(minLon)) {
          return null;
        }
        return [minLon, minLat, maxLon, maxLat];
      },

      extentFromGeometry(geom) {
        if (!geom) {
          return null;
        }
        try {
          if (geom.type === 'Point' && Array.isArray(geom.coordinates)) {
            const [lon, lat] = geom.coordinates.map(Number);
            if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
              return null;
            }
            const d = 0.002;
            return [lon - d, lat - d, lon + d, lat + d];
          }
          if (geom.type === 'Polygon' && geom.coordinates?.[0]) {
            return this.ringToExtent(geom.coordinates[0]);
          }
          if (geom.type === 'MultiPolygon' && geom.coordinates?.[0]?.[0]) {
            return this.ringToExtent(geom.coordinates[0][0]);
          }
          if (Array.isArray(geom.coordinates)) {
            const flat = geom.coordinates.flat(3);
            if (flat.length >= 2 && typeof flat[0] === 'number') {
              const pairs = [];
              for (let i = 0; i + 1 < flat.length; i += 2) {
                pairs.push([flat[i], flat[i + 1]]);
              }
              return this.ringToExtent(pairs);
            }
          }
        } catch (e) {
          console.warn('[AltitudeOrthoBoxMap] extentFromGeometry', e);
        }
        return null;
      },

      ringToExtent(ring) {
        let minLon = Infinity;
        let minLat = Infinity;
        let maxLon = -Infinity;
        let maxLat = -Infinity;
        (ring || []).forEach(c => {
          const lon = Number(c[0]);
          const lat = Number(c[1]);
          if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
            return;
          }
          minLon = Math.min(minLon, lon);
          minLat = Math.min(minLat, lat);
          maxLon = Math.max(maxLon, lon);
          maxLat = Math.max(maxLat, lat);
        });
        if (!Number.isFinite(minLon) || maxLon <= minLon) {
          return null;
        }
        return [minLon, minLat, maxLon, maxLat];
      },

      onDrawEnd(feature) {
        const geom = feature.getGeometry();
        const coords = geom && geom.getCoordinates && geom.getCoordinates()[0];
        const L_map_m = longestSideMetersFromLonLatRing(coords);
        if (L_map_m == null) {
          this.L_map_m = null;
          this.orthoBox = null;
          this.$emit('box-change', null);
          return;
        }
        this.L_map_m = L_map_m;
        this.orthoBox = {
          ring: coords,
          extent: geom.getExtent().slice(),
          L_map_m
        };
        this.$emit('box-change', this.orthoBox);
      },

      clearBox() {
        if (this.drawSource) {
          this.drawSource.clear();
        }
        this.L_map_m = null;
        this.orthoBox = null;
        this.$emit('box-change', null);
      },

      reset() {
        this.clearBox();
      }
    }
  };
</script>
